import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tagsService } from '../api/tags.service';
import { TAG_KEYS } from './queryKeys';
import type { TagFilters } from '../types';

/**
 * Fetches the paginated tag list for a project.
 *
 * @param projectId - Active project ID; pass `null` to keep the query disabled.
 * @param filters - Pagination and search params forwarded to the API.
 * @returns TanStack Query result containing a `TagPagedResult`.
 *
 * @remarks
 * - Disabled unless both `activeOrganizationId` (org store) and `projectId` are truthy.
 * - `staleTime: 300_000` (5 min) — tags change infrequently; avoids redundant network calls.
 * - `keepPreviousData` prevents the list from blanking out while paginating or searching.
 */
export function useTags(projectId: string | null, filters: TagFilters = {}) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useQuery({
    queryKey: TAG_KEYS.list(orgId ?? '', projectId ?? '', filters),
    queryFn: ({ signal }) => tagsService.getList(projectId!, filters, signal),
    staleTime: 300_000,
    enabled: !!orgId && !!projectId,
    placeholderData: keepPreviousData,
  });
}
